import { useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import Avatar from "@/components/Avatar";
import { api } from "@/lib/api";
import { CONTENT_STYLES, LANGUAGES, GAMLY_PERSONALITIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { UserCircleIcon, TrophyIcon, UsersThreeIcon, CrownIcon, SignOutIcon, ArrowRightIcon, GlobeIcon } from "@phosphor-icons/react";

export default function Settings() {
  const { user, setUser, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [style, setStyle] = useState(user?.content_style || "casual_fan");
  const [personality, setPersonality] = useState(user?.personality || "broadcaster");
  const [brand, setBrand] = useState(user?.brand || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/preferences", { language_preference: language, content_style: style, personality, brand });
      setUser(res.data);
      await refresh();
    } catch (err) {
      captureError("settings save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const planLabel = { free: "Free Fan", pro: "Pro Fan", press_box_pro: "Press Box Pro" }[user?.plan] || "Free Fan";
  const planAccent = { free: "#FFFFFF", pro: "#0A84FF", press_box_pro: "#FDB927" }[user?.plan] || "#FFFFFF";

  return (
    <AppLayout>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">Settings</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Your Account</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile card */}
        <section className="lg:col-span-2 p-5 rounded-md bg-[#121212] border border-white/10" data-testid="settings-profile">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-3">Profile</p>
          <div className="flex items-center gap-4 mb-5">
            <Avatar src={user?.picture} name={user?.name} size={56} />
            <div>
              <p className="font-display font-bold text-2xl uppercase tracking-tighter">{user?.name}</p>
              <p className="text-sm text-white/55">{user?.email}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: planAccent }}>{planLabel}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="settings-language" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Content Style</label>
              <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="settings-style" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                {CONTENT_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Brand / Show Name (Press Box)</label>
            <input
              data-testid="settings-brand"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g. Maxima Deportes"
              className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Gamly Personality — applies to all AI outputs */}
          <div className="mb-4" data-testid="settings-personality-section">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">
              Gamly Personality <span className="text-[#0A84FF]">· applies everywhere</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {GAMLY_PERSONALITIES.map((p) => {
                const active = personality === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPersonality(p.value)}
                    data-testid={`personality-${p.value}`}
                    className={`text-left p-3 rounded-md border transition-colors ${
                      active
                        ? "border-[#0A84FF] bg-[#0A84FF]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <p.icon weight="bold" size={14} className={active ? "text-[#0A84FF]" : "text-white/60"} />
                      <span className="font-display font-bold text-xs uppercase tracking-tight">{p.label}</span>
                    </div>
                    <p className="text-[10px] text-white/45 leading-snug">{p.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={save} disabled={saving} data-testid="settings-save" className="px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </section>

        {/* Plan card */}
        <aside className="p-5 rounded-md bg-[#121212] border border-white/10" data-testid="settings-plan">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-3">Subscription</p>
          <CrownIcon weight="fill" size={28} className="mb-3" style={{ color: planAccent }} />
          <p className="font-display font-bold text-2xl uppercase tracking-tighter mb-1">{planLabel}</p>
          <p className="text-sm text-white/55 mb-5">
            {user?.plan === "press_box_pro" && "All Press Box production tools unlocked."}
            {user?.plan === "pro" && "Unlimited AI + advanced fan features."}
            {(!user?.plan || user?.plan === "free") && "Basic scores + limited AI."}
          </p>
          {user?.plan !== "press_box_pro" && (
            <Link to="/pricing" data-testid="settings-upgrade" className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-bold uppercase tracking-wider">
              Upgrade <ArrowRightIcon />
            </Link>
          )}
        </aside>
      </div>

      {/* Quick links to Teams / Players / Fan DNA */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <LinkTile to="/teams" icon={TrophyIcon} title="My Teams" desc="Pick the teams that shape your feed." testId="link-teams" />
        <LinkTile to="/players" icon={UsersThreeIcon} title="Player Watchlist" desc="Track favorite players + stats." testId="link-players" />
        <LinkTile to="/onboarding" icon={GlobeIcon} title="Re-do Fan DNA" desc="Re-run the 5-step onboarding wizard." testId="link-onboarding" />
      </div>

      {/* Sign out */}
      <div className="mt-8">
        <button onClick={onLogout} data-testid="settings-logout" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#FF453A]/40 text-[#FF453A] text-xs font-bold uppercase tracking-wider hover:bg-[#FF453A]/10">
          <SignOutIcon /> Sign out
        </button>
      </div>
    </AppLayout>
  );
}

function LinkTile({ to, icon: Icon, title, desc, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="p-4 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors block"
    >
      <Icon weight="bold" size={20} className="text-[#0A84FF] mb-2" />
      <p className="font-display font-bold text-sm uppercase tracking-tight mb-1">{title}</p>
      <p className="text-xs text-white/55">{desc}</p>
    </Link>
  );
}
